import { langue } from '../i18n';
import { ouvrirPageExterne } from './pageExterne';

/**
 * Ouvrir un document legal, sans quitter l'application.
 *
 * # Pourquoi une seule fonction
 *
 * Ces liens sont appeles depuis trois endroits — l'inscription, l'ecran
 * de reconsentement, les reglages. Trois appels, trois occasions de s'y
 * prendre differemment : l'un ouvrirait Safari, l'autre remplacerait la
 * vue en cours. Ici il n'y a qu'une porte.
 *
 * # Pourquoi pas Safari
 *
 * Quitter l'application pour lire des conditions, c'est perdre l'ecran
 * d'inscription a moitie rempli — et, sur l'ecran de reconsentement, se
 * retrouver a revenir dans une application qu'on n'a pas encore le droit
 * d'utiliser. Le navigateur interne se referme sur ce qu'on avait laisse.
 *
 * # Pourquoi pas une vue web nue
 *
 * Une `WKWebView` posee a la main n'a ni barre d'adresse, ni bouton de
 * fermeture, ni indication du domaine visite. Sur un document juridique,
 * pouvoir verifier d'ou vient la page n'est pas un detail. Le greffon
 * ouvre la vue du systeme, qui montre le domaine et se ferme d'un geste.
 *
 * # Quand ca echoue
 *
 * Rien ne garantit qu'une ouverture aboutisse — greffon absent, vue
 * refusee, adresse invalide. On ne laisse alors pas l'utilisateur devant
 * un bouton mort : l'appelant recoit `false` et montre l'adresse, qu'on
 * peut copier et ouvrir ailleurs.
 */
export function ouvrirDocumentLegal(url: string): Promise<boolean> {
  return ouvrirPageExterne(documentDansLaLangue(url));
}

/** Les pages legales qui existent aussi traduites, sous `/en/` et `/es/`. */
const TRADUITES = new Set(['cgu', 'confidentialite', 'mentions-legales', 'securite-des-enfants', 'aide', 'legal']);

/**
 * L'adresse d'un document dans la langue du membre.
 *
 * Les traductions vivent a cote des francaises, sous `/{langue}/`, avec
 * le meme nom : on y envoie un membre qui lit l'application dans cette
 * langue. La version francaise fait foi, et chaque page traduite le dit
 * et y renvoie. Une adresse inconnue passe telle quelle.
 */
export function documentDansLaLangue(url: string): string {
  if (langue === 'fr') return url;
  try {
    const adresse = new URL(url, window.location.origin);
    const page = adresse.pathname.replace(/^\/+|\.html$/g, '');
    if (!TRADUITES.has(page)) return url;
    adresse.pathname = `/${langue}/${page}`;
    return adresse.toString();
  } catch {
    return url;
  }
}
